/**
 * #4447 GUSSTATION: what the client does around a PIN session. The server
 * decides everything (who is signed in, what a PIN session may reach, when it
 * ends); this file only moves the browser to the right screen afterwards.
 */
import { apiMutate } from "./api";
import { clearRbacCache } from "@/hooks/use-rbac";
import { writeStationHome } from "./station-home";
import type { StationHomePage } from "@shared/station";

/** Admin opens on its Stations tab from this URL (Admin reads ?tab=). Only the Admin page's own check may offer it. */
export const ADMIN_STATIONS_PATH = "/admin?tab=stations";

/**
 * A device that has signed in with a PIN once. Informational only: when a
 * session ends on this device, RequireAuth sends it to the keypad instead of
 * the public landing page. The server ignores it.
 */
export const KIOSK_KEY = "crosbe-station-kiosk";

const kioskListeners = new Set<() => void>();

/** #4974: told when this tab sets or clears the mark (another tab's change arrives as a `storage` event). */
export function onKioskMarkChange(listener: () => void): () => void {
  kioskListeners.add(listener);
  return () => {
    kioskListeners.delete(listener);
  };
}

function kioskMarkChanged(): void {
  for (const listener of kioskListeners) listener();
}

export function markKioskDevice(): void {
  try {
    localStorage.setItem(KIOSK_KEY, "1");
  } catch {
    // blocked storage: the device lands on /welcome like any other
  }
  kioskMarkChanged();
}

export function clearKioskDevice(): void {
  try {
    localStorage.removeItem(KIOSK_KEY);
  } catch {
    // nothing to clear
  }
  kioskMarkChanged();
}

export function isKioskDevice(): boolean {
  try {
    return localStorage.getItem(KIOSK_KEY) === "1";
  } catch {
    return false;
  }
}

/** The person signed in with a PIN: remember the home page on this device and land on it. */
export function landAfterPinSignIn(homePage: StationHomePage): void {
  clearRbacCache();
  writeStationHome(homePage);
  markKioskDevice();
  window.location.href = `/reminders?station=${encodeURIComponent(homePage)}`;
}

/**
 * Switch person: end the PIN session on the server (its cookies are cleared in
 * the response) and return to the keypad. The keypad is the right place even
 * when the request fails: the cookies may already be gone, and /station reads
 * the truth from the server.
 */
export async function switchPerson(): Promise<void> {
  try {
    await apiMutate("/api/station/switch", "POST");
  } catch {
    // already ended, or unreachable: the keypad shows which
  }
  clearRbacCache();
  window.location.href = "/station";
}
